"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergeDisjointComponents = mergeDisjointComponents;
exports.createMergedComponent = createMergedComponent;
const _ = require("lodash");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
function getDefaultOpts() {
    return {
        mergeSignalsWithSamePinRef: true,
    };
}
function mergeDisjointComponents(project, inputOpts = {}) {
    const opts = { ...getDefaultOpts(), ...inputOpts };
    const disjointComponents = new Map();
    const componentsModules = new Map();
    for (const m of project.modules) {
        const symbolComponentPairs = new Set();
        let isDisjoint = false;
        for (const instance of m.instances) {
            const design = project.designs[instance.designId];
            if (design.modelType !== 'schematic') {
                continue;
            }
            const module = design.modules.find((m) => m.ref === instance.ref);
            if (!module) {
                throw new Error(`Module ${instance.ref} not found in design ${instance.designId}`);
            }
            if (!componentsModules.has(module.componentRef)) {
                componentsModules.set(module.componentRef, [m]);
            }
            else {
                componentsModules.get(module.componentRef).push(m);
            }
            const identifier = getDisjointComponentUid(module);
            symbolComponentPairs.add(identifier);
            if (module.componentRef !== m.componentRef) {
                isDisjoint = true;
            }
        }
        if (isDisjoint) {
            const combined = Array.from(symbolComponentPairs.values()).sort().join(',');
            if (!disjointComponents.has(combined)) {
                disjointComponents.set(combined, [m]);
            }
            else {
                disjointComponents.get(combined).push(m);
            }
        }
    }
    for (const [key, modules] of disjointComponents) {
        const disjointComponentMessageResult = {
            type: 'disjoint-component-merge',
            message: '',
            innerResults: [],
        };
        const newComponent = createMergedComponent(modules, project, opts, disjointComponentMessageResult);
        disjointComponentMessageResult.message = `Merged disjoint components into ${newComponent.newRef}`;
        disjointComponentMessageResult.newComponentRef = newComponent.newRef;
        for (const m of modules) {
            m.componentRef = newComponent.newRef;
            for (const instance of m.instances) {
                const design = project.designs[instance.designId];
                if (design.modelType === 'schematic') {
                    updateSchematicModule(design, instance, project, newComponent, disjointComponentMessageResult);
                }
                else if (design.modelType === 'pcb') {
                    updatePcbModule(design, instance, project, newComponent);
                }
            }
        }
        project.processingResults.innerResults.push(disjointComponentMessageResult);
        disjointComponentMessageResult.pinMapping = sigma_engine_shared_1.Utils.deepConvertMaps(newComponent.symbolPinMapping);
    }
    for (const [component, modules] of componentsModules) {
        if (modules.every((m) => m.componentRef !== component)) {
            delete project.components[component];
        }
    }
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(project.processingResults);
    return project;
}
function updateSchematicModule(design, instance, project, newComponent, messageResult) {
    const module = design.modules.find((m) => m.ref === instance.ref);
    if (!module) {
        throw new Error(`Module ${instance.ref} not found in design ${instance.designId}`);
    }
    const oldComponent = project.components[module.componentRef];
    if (!oldComponent) {
        throw new Error(`Component ${module.componentRef} not found in project`);
    }
    const oldSymbol = oldComponent.symbols.find((s) => s.ref === module.footprintRef);
    if (!oldSymbol) {
        throw new Error(`Symbol ${module.footprintRef} not found in component ${oldComponent.name}`);
    }
    const newSymbolMapping = newComponent.symbolPinMapping.get(`${module.componentRef}::${module.footprintRef}`);
    if (!newSymbolMapping) {
        throw new Error(`New symbol not found for ${module.footprintRef}`);
    }
    module.footprintRef = newSymbolMapping.newSymbolRef;
    module.componentRef = newComponent.newRef;
    const oldPinNets = module.pinNets;
    module.pinNets = {};
    const updateMessageResult = {
        type: 'schematic-module-updated',
        message: `Updated schematic module ${module.ref} (${module.name})`,
        moduleRef: module.ref,
        oldComponentRef: oldComponent.name,
        newComponentRef: newComponent.newRef,
        oldSymbolRef: oldSymbol.ref,
        newSymbolRef: newSymbolMapping.newSymbolRef,
        innerResults: [],
    };
    for (const [oldPinRef, newPinRef] of newSymbolMapping.pinMapping) {
        const oldNet = oldPinNets[oldPinRef]?.valueOf();
        const designNet = design.nets[oldNet];
        if (designNet === undefined) {
            if (oldNet !== undefined) {
                updateMessageResult.severity = sigma_engine_shared_1.DesignModels.BoundaryLevel.Error;
                updateMessageResult.innerResults?.push({
                    type: 'net-not-found',
                    message: `Net ${oldNet} not found in design ${instance.designId}`,
                    severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Error,
                    netName: oldNet,
                });
            }
            continue;
        }
        module.pinNets[newPinRef] = oldPinNets[oldPinRef];
        for (const connection of designNet.connections) {
            if (connection.moduleRef === module.ref && connection.pinRef === oldPinRef) {
                connection.pinRef = newPinRef;
            }
        }
    }
    if (updateMessageResult.innerResults?.length === 0) {
        delete updateMessageResult.innerResults;
    }
    messageResult.innerResults.push(updateMessageResult);
}
function getDisjointComponentUid(module) {
    return `\0${module.componentRef}::${module.footprintRef}\0`;
}
function createMergedComponent(modules, project, opts, messageResult) {
    const moduleComponentRefs = Array.from(new Set(modules.map((m) => m.componentRef)));
    messageResult.mergedComponentRefs = moduleComponentRefs;
    const pcbModules = modules
        .flatMap((m) => m.instances.flatMap((i) => {
        const design = project.designs[i.designId];
        if (design.modelType !== 'pcb') {
            return null;
        }
        return design.modules.find((m2) => m2.ref === i.ref);
    }))
        .filter((m) => m !== null && m !== undefined);
    const footprintNames = new Set(pcbModules.map((m) => m.footprintRef));
    if (footprintNames.size > 1) {
        throw new Error('Cannot merge modules with different footprint names');
    }
    let cpNameSuffix = '';
    const cpBaseName = `Merged_${moduleComponentRefs.join('::')}`;
    while (`${cpBaseName}${cpNameSuffix}` in project.components) {
        cpNameSuffix = sigma_engine_shared_1.Utils.randomAlphaStr(4);
    }
    const newCp = {
        name: `${cpBaseName}${cpNameSuffix}`,
        numPins: 0,
        numPads: 0,
        footprints: [],
        symbols: [],
        signals: [],
        metadata: {},
    };
    const result = {
        newRef: newCp.name,
        component: newCp,
        footprintName: pcbModules[0]?.footprintRef,
        symbolPinMapping: new Map(),
    };
    let useFootprint;
    if (pcbModules.length > 0) {
        const firstModule = pcbModules[0];
        useFootprint = project.components[firstModule.componentRef]?.footprints.find((f) => f.ref === firstModule.footprintRef);
        if (useFootprint) {
            newCp.footprints.push(useFootprint);
        }
    }
    for (const m of modules) {
        for (const instance of m.instances) {
            const design = project.designs[instance.designId];
            if (design?.modelType !== 'schematic') {
                continue;
            }
            const designModule = design.modules.find((m2) => m2.ref === instance.ref);
            if (!designModule) {
                throw new Error(`Module ${instance.ref} not found in design ${instance.designId}`);
            }
            const oldComponent = project.components[designModule.componentRef];
            if (!oldComponent) {
                throw new Error(`Component ${designModule.componentRef} not found in project`);
            }
            const oldSymbol = oldComponent.symbols.find((s) => s.ref === designModule.footprintRef);
            if (!oldSymbol) {
                throw new Error(`Symbol ${designModule.footprintRef} not found in component ${oldComponent.name}`);
            }
            const combinedRef = `${designModule.componentRef}::${designModule.footprintRef}`;
            if (result.symbolPinMapping.has(combinedRef)) {
                continue;
            }
            if (oldComponent.model) {
                if (newCp.model) {
                    if (!_.isEqual(newCp.model, oldComponent.model)) {
                        throw new Error(`Cannot merge components with different sigma models: ${newCp.model.modelId} and ${oldComponent.model.modelId}`);
                    }
                }
                else {
                    newCp.model = structuredClone(oldComponent.model);
                }
            }
            let suffix = '';
            while (newCp.symbols.some((s) => s.ref === `${oldSymbol.ref}${suffix}`)) {
                suffix = sigma_engine_shared_1.Utils.randomAlphaStr(3);
            }
            const newMapping = {
                newSymbolRef: `${designModule.footprintRef}${suffix}`,
                pinMapping: new Map(),
            };
            result.symbolPinMapping.set(combinedRef, newMapping);
            for (const signal of oldComponent.signals) {
                if (!signal.pinRefs.some((pinRef) => pinRef !== null && oldSymbol.pins.some((p) => p.ref === pinRef))) {
                    continue;
                }
                let sid = signal.id;
                while (newCp.signals.some((s) => s.id === sid)) {
                    sid = `${signal.id}_${sigma_engine_shared_1.Utils.randomAlphaStr(3)}`;
                }
                const newPinRefs = signal.pinRefs
                    .filter((pinRef) => oldSymbol.pins.some((p) => p.ref === pinRef))
                    .map((oldSignalPinRef) => {
                    if (opts.mergeSignalsWithSamePinRef) {
                        return [oldSignalPinRef, oldSignalPinRef];
                    }
                    let suffix = '';
                    while (newCp.symbols.some((s) => s.pins.some((p) => p.ref === `${oldSignalPinRef}${suffix}`))) {
                        suffix = sigma_engine_shared_1.Utils.randomAlphaStr(3);
                    }
                    return [oldSignalPinRef, `${oldSignalPinRef}${suffix}`];
                });
                const matchingExistingSignals = newCp.signals.filter((s) => s.pinRefs.some((p) => newPinRefs.some(([, newP]) => p === newP)));
                if (matchingExistingSignals.length > 1) {
                    throw new Error(`Cannot merge signals with same pin refs: ${matchingExistingSignals.map((s) => s.id).join(', ')}`);
                }
                else if (matchingExistingSignals.length === 1) {
                    const existing = matchingExistingSignals[0];
                    existing.pinRefs = Array.from(new Set([...existing.pinRefs, ...newPinRefs.map(([, newP]) => newP)]));
                    existing.padRefs = Array.from(new Set([...existing.padRefs, ...signal.padRefs]));
                }
                else {
                    newCp.signals.push({
                        ...signal,
                        id: sid,
                        pinRefs: newPinRefs.map(([, pr]) => pr),
                        padRefs: useFootprint ? signal.padRefs.filter((padRef) => useFootprint.pads.some((p) => p.ref === padRef)) : [],
                    });
                }
                for (const [oldPin, newPin] of newPinRefs) {
                    if (oldPin === null) {
                        continue;
                    }
                    if (newPin === null) {
                        throw new Error('New pin is null');
                    }
                    newMapping.pinMapping.set(oldPin, newPin);
                }
            }
            newCp.symbols.push({
                ...oldSymbol,
                ref: newMapping.newSymbolRef,
                pins: oldSymbol.pins.map((p) => {
                    const newPinRef = newMapping.pinMapping.get(p.ref) ?? p.ref;
                    return { ...p, ref: newPinRef };
                }),
            });
        }
    }
    newCp.numPads = newCp.footprints.reduce((p, c) => p + c.pads.length, 0);
    newCp.numPins = newCp.symbols.reduce((p, c) => p + c.pins.length, 0);
    project.components[newCp.name] = newCp;
    return result;
}
function updatePcbModule(design, instance, project, newComponent) {
    if (!newComponent.footprintName) {
        throw new Error('No footprint name');
    }
    const module = design.modules.find((m) => m.ref === instance.ref);
    if (!module) {
        throw new Error(`Module ${instance.ref} not found in design ${instance.designId}`);
    }
    module.componentRef = newComponent.newRef;
    module.footprintRef = newComponent.footprintName;
}
//# sourceMappingURL=mergeDisjointComponentsUtil.js.map